import { useBalance, useReadContracts } from 'wagmi';
import { erc20Abi } from 'viem';
import { QueryOptions } from 'utils/useQueryUtils';
import { ETH_ZERO_ADDRESS } from 'utils/constants/ethConst';
import { isNativeTokenAddress } from 'structure/pv2/vault/utils/nativeDeposit';

interface UseTokenBalance extends QueryOptions {
  address?: `0x${string}`;
  token: `0x${string}`;
  chainId: number;
}

type TokenBalance = {
  value: bigint;
  decimals: number;
  symbol: string;
} | null;

export const useTokenBalance = (params: UseTokenBalance) => {
  const { address, token, chainId, query } = params;
  const isNativeToken = isNativeTokenAddress(token);

  const nativeBalance = useBalance({
    address,
    chainId,
    query: {
      enabled:
        Boolean(address && address !== ETH_ZERO_ADDRESS && chainId) &&
        isNativeToken &&
        (query?.enabled ?? true),
      staleTime: query?.staleTime,
      gcTime: query?.gcTime,
      refetchInterval: query?.refetchInterval,
    },
  });

  const tokenBalance = useReadContracts({
    query: {
      enabled:
        Boolean(address && address !== ETH_ZERO_ADDRESS && token && chainId) &&
        !isNativeToken &&
        (query?.enabled ?? true),
      staleTime: query?.staleTime,
      gcTime: query?.gcTime,
      refetchInterval: query?.refetchInterval,
      select: (data): TokenBalance => {
        if (!data) return null;

        const [value, decimals, symbol] = data;
        return { value, decimals, symbol };
      },
    },
    allowFailure: false,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    contracts: [
      {
        address: token,
        abi: erc20Abi,
        chainId,
        functionName: 'balanceOf',
        args: address ? [address] : undefined,
      },
      {
        address: token,
        abi: erc20Abi,
        chainId,
        functionName: 'decimals',
      },
      {
        address: token,
        abi: erc20Abi,
        chainId,
        functionName: 'symbol',
      },
    ],
  });

  return isNativeToken ? nativeBalance : tokenBalance;
};
